import { Box, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EaDashboardFilters from './components/EaDashboardFilters.component';
import ExceptionCommentsGrid from 'modules/dashboard/components/ExceptionCommentsGrid.component';
import ExpenseFollowUpGrid from 'modules/dashboard/components/ExpenseFollowUpGrid.component';
import MyFilesContainer from 'modules/dashboard/myFiles/myFiles.container';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import RecurringPayablesGrid from 'modules/dashboard/components/RecurringPayablesGrid.component';
import UserSelector from 'modules/dashboard/components/UserSelector.component';
import VoucherFollowUpGrid from './components/VoucherFollowUpGrid.component';

const useStyles = makeStyles((theme) => ({
  dashboardContainer: {
    padding: theme.spacing(3),
  },
  tabsContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  sectionPaper: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
}));

const EaDashboardComponent = ({
  users,
  selectedUserId,
  loggedInUserId,
  onUserChange,
  clients,
  filters,
  onFilterChange,
  isLoading,
  expenseFollowUp,
  voucherFollowUp,
  recurringPayables,
  exceptionComments,
  isLoadingExpenses,
  isLoadingVouchers,
  isLoadingRecurringPayables,
  isLoadingExceptionComments,
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Employee/File options come from the rows already loaded for this user, so the
  // list is always scoped to files the selected user actually has work on.
  const fileOptions = useMemo(() => {
    const allRows = [...expenseFollowUp, ...voucherFollowUp, ...recurringPayables, ...exceptionComments];
    const optionsByFileId = new Map();

    allRows.forEach((row) => {
      if (row.fileId && !optionsByFileId.has(row.fileId)) {
        optionsByFileId.set(row.fileId, {
          fileId: row.fileId,
          authId: row.authId || '',
          eeName: row.eeName || '',
        });
      }
    });

    return [...optionsByFileId.values()].sort((firstFile, secondFile) =>
      `${firstFile.authId}`.localeCompare(`${secondFile.authId}`),
    );
  }, [expenseFollowUp, voucherFollowUp, recurringPayables, exceptionComments]);

  // The dashboard rows carry clientName rather than a client id, so the selected
  // client is matched by name — the same approach the ops manager dashboard uses.
  const selectedClientNames = useMemo(() => {
    if (!filters.clientId) return null;

    const selectedClient = clients.find((client) => client.clientRecordId === filters.clientId);
    if (!selectedClient) return null;

    return [selectedClient.shortName, selectedClient.legalName]
      .filter(Boolean)
      .map((name) => name.toLowerCase());
  }, [clients, filters.clientId]);

  const applyFilters = useCallback((rows) => {
    let filtered = rows;

    if (selectedClientNames) {
      filtered = filtered.filter((row) => selectedClientNames.includes((row.clientName || '').toLowerCase()));
    }

    if (filters.fileId) {
      filtered = filtered.filter((row) => row.fileId === filters.fileId);
    }

    return filtered;
  }, [selectedClientNames, filters.fileId]);

  const filteredExpenseFollowUp = useMemo(() => applyFilters(expenseFollowUp), [applyFilters, expenseFollowUp]);
  const filteredVoucherFollowUp = useMemo(() => applyFilters(voucherFollowUp), [applyFilters, voucherFollowUp]);
  const filteredRecurringPayables = useMemo(() => applyFilters(recurringPayables), [applyFilters, recurringPayables]);
  const filteredExceptionComments = useMemo(() => applyFilters(exceptionComments), [applyFilters, exceptionComments]);

  return (
    <Box className={classes.dashboardContainer}>
      <Typography variant="h4" gutterBottom>
        EA Dashboard
      </Typography>

      {/* User selector for testing and IT support */}
      <UserSelector
        users={users}
        selectedUserId={selectedUserId}
        loggedInUserId={loggedInUserId}
        onUserChange={onUserChange}
        isLoading={isLoading}
      />

      {/* Tabs */}
      <Paper className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="My Tasks" />
          <Tab label="My Files" />
        </Tabs>
      </Paper>

      {/* Tab Panel 0: My Tasks */}
      {activeTab === 0 && (
        <Box className={classes.tabPanel}>
          {/* Filters for the tables on this tab */}
          <EaDashboardFilters
            filters={filters}
            onFilterChange={onFilterChange}
            clients={clients}
            files={fileOptions}
            isLoading={isLoading}
          />

          {/* Expense Follow-up Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              Expense Follow-up
            </Typography>

            <ExpenseFollowUpGrid
              expenses={filteredExpenseFollowUp}
              isLoading={isLoadingExpenses}
              clients={clients}
            />
          </Paper>

          {/* Voucher Follow-up Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              Voucher Follow-up
            </Typography>

            <VoucherFollowUpGrid
              vouchers={filteredVoucherFollowUp}
              isLoading={isLoadingVouchers}
            />
          </Paper>

          {/* Recurring Payables Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              Active Recurring Payables
            </Typography>

            <RecurringPayablesGrid
              recurringPayables={filteredRecurringPayables}
              isLoading={isLoadingRecurringPayables}
            />
          </Paper>

          {/* Exceptions Section */}
          <Paper className={classes.sectionPaper}>
            <Typography variant="h5" gutterBottom>
              Exceptions
            </Typography>

            <ExceptionCommentsGrid
              exceptions={filteredExceptionComments}
              isLoading={isLoadingExceptionComments}
              clients={clients}
            />
          </Paper>
        </Box>
      )}

      {/* Tab Panel 1: My Files */}
      {activeTab === 1 && (
        <Box className={classes.tabPanel}>
          {/* Same tables and coverage workflow as the RC dashboard; isRCRole tells
              the backend to treat coverage assignments as expense analyst coverage */}
          <MyFilesContainer
            selectedUserId={selectedUserId}
            isRCRole={false}
          />
        </Box>
      )}
    </Box>
  );
};

EaDashboardComponent.propTypes = {
  users: PropTypes.array,
  selectedUserId: PropTypes.number,
  loggedInUserId: PropTypes.number,
  onUserChange: PropTypes.func.isRequired,
  clients: PropTypes.array,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  expenseFollowUp: PropTypes.array,
  voucherFollowUp: PropTypes.array,
  recurringPayables: PropTypes.array,
  exceptionComments: PropTypes.array,
  isLoadingExpenses: PropTypes.bool,
  isLoadingVouchers: PropTypes.bool,
  isLoadingRecurringPayables: PropTypes.bool,
  isLoadingExceptionComments: PropTypes.bool,
};

EaDashboardComponent.defaultProps = {
  users: [],
  selectedUserId: null,
  loggedInUserId: null,
  clients: [],
  filters: {},
  isLoading: false,
  expenseFollowUp: [],
  voucherFollowUp: [],
  recurringPayables: [],
  exceptionComments: [],
  isLoadingExpenses: false,
  isLoadingVouchers: false,
  isLoadingRecurringPayables: false,
  isLoadingExceptionComments: false,
};

export default EaDashboardComponent;
